import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import { useRunSingleModelEvalMutation } from 'services/api/conversationEvals.api';
import type { ConversationEval } from 'services/types/conversationEvals';
import { AI_MODELS } from 'utils/aiModels';

interface SingleModelFormProps {
  conversationEval: ConversationEval;
  onClose: () => void;
  onRunEvaluation: (params: { targetModel: string; evaluationModel: string; threshold: number }) => void;
}

const SingleModelForm: React.FC<SingleModelFormProps> = ({
  conversationEval,
  onClose,
  onRunEvaluation = undefined,
}) => {
  const [targetModel, setTargetModel] = useState<string>('gpt-5.2-none');
  const [evaluationModel, setEvaluationModel] = useState<string>('gemini-3.1-pro-preview-low');
  const [threshold, setThreshold] = useState<number>(7.0);

  const [runSingleModelEval, { isLoading }] = useRunSingleModelEvalMutation();

  const handleSubmit = async () => {
    if (onRunEvaluation) {
      // Use callback approach (similar to BlindComparisonForm)
      onRunEvaluation({
        targetModel,
        evaluationModel,
        threshold,
      });
      onClose();
    } else {
      // Fallback to direct API call
      try {
        await runSingleModelEval({
          conversationEvalId: conversationEval.id,
          targetModel,
          evaluationModel,
          threshold,
          businessUnitIds: conversationEval.businessUnitIds,
        }).unwrap();
        onClose();
      } catch (error) {
        console.error('Failed to run single model evaluation:', error);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 400 }}>
      <Typography variant="h6" gutterBottom>
        Configurar Evaluación de Modelo Único
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        Evalúa la respuesta de un modelo AI contra un estándar de calidad absoluto.
      </Typography>
      <FormControl fullWidth>
        <InputLabel>Modelo a Evaluar</InputLabel>
        <Select value={targetModel} label="Modelo a Evaluar" onChange={(e) => setTargetModel(e.target.value)}>
          {AI_MODELS.map((model) => (
            <MenuItem key={model} value={model}>
              {model}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Modelo Evaluador</InputLabel>
        <Select value={evaluationModel} label="Modelo Evaluador" onChange={(e) => setEvaluationModel(e.target.value)}>
          {AI_MODELS.map((model) => (
            <MenuItem key={model} value={model}>
              {model}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        label="Threshold (1-10)"
        type="number"
        value={threshold}
        onChange={(e) => setThreshold(Number(e.target.value))}
        helperText="Puntaje mínimo requerido para considerar la respuesta como exitosa"
        slotProps={{
          htmlInput: { min: 1, max: 10, step: 0.1 },
        }}
      />
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        {}
        <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Ejecutando...' : 'Ejecutar Evaluación'}
        </Button>
      </Box>
    </Box>
  );
};

export default SingleModelForm;
